import { useActiveInterval } from '@/hooks/useActiveInterval';
import { getStoredAuthToken, showLoginExpiredModal } from '@/requestErrorConfig';
import { request } from '@umijs/max';
import React, { useCallback, useEffect, useRef } from 'react';

/** 只检查已有登录；强退或异地登录通过统一 401 提示及时反馈到停留中的页面。 */
const SessionHeartbeat: React.FC = () => {
  const pending = useRef<AbortController>();
  const checkSession = useCallback(async () => {
    const token = getStoredAuthToken();
    if (!token || pending.current || document.visibilityState !== 'visible') return;
    const controller = new AbortController();
    pending.current = controller;
    try {
      await request('/api/system/session/status', {
        method: 'GET', signal: controller.signal, pageLoading: false, skipErrorHandler: true,
        handleAuthFailureLocally: true,
      });
    } catch (error: any) {
      if (controller.signal.aborted || getStoredAuthToken() !== token) return;
      if (error?.response?.status === 401 || error?.info?.errorCode === 401) {
        showLoginExpiredModal(error?.response?.data?.message || error?.response?.data?.errorMessage || error?.info?.message || error?.info?.errorMessage);
      }
      // 短暂网络故障不打断当前操作，下一次可见心跳继续检查。
    } finally {
      if (pending.current === controller) pending.current = undefined;
    }
  }, []);

  useActiveInterval(checkSession, 30_000);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') void checkSession(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      pending.current?.abort();
      pending.current = undefined;
    };
  }, [checkSession]);
  return null;
};

export default SessionHeartbeat;
